// CB:BEGIN fr-014-realtor-client-portal
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';

const RealtorClientPortal: React.FC<{ token: string; onNavigate?: (screen: string) => void }> = ({ token, onNavigate }) => {
  const { isSignedIn, getToken } = useAuth();
  const [payload, setPayload] = useState<{
    quoteRequestId: string;
    address: string;
    bids: Array<{ id: string; amountCents?: number; scopeNotes?: string; contractorCompany?: string }>;
    coBranding: { realtorName: string };
    inviteExpiresAt: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    const fetchPayload = async () => {
      try {
        const base = getApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${base}/api/realtor/portal/${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(String(data.error || 'Invalid or expired link'));
          return;
        }
        const data = await res.json();
        setPayload(data);
      } catch (e) {
        setError('Failed to load portal');
      }
    };
    fetchPayload();
  }, [token]);

  useEffect(() => {
    if (!isSignedIn && payload && !error) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/sign-in?redirect_url=${returnUrl}`;
    }
  }, [isSignedIn, payload, error]);

  const handleAccept = async (bidId: string) => {
    setAccepting(bidId);
    try {
      const authToken = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/portal/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({ bidId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.project?.id && onNavigate) onNavigate(`/dashboard`);
        else if (data.project?.id) window.location.href = '/dashboard';
        else alert('Bid accepted. Project created.');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Only homeowners can accept a bid. Please sign in as a homeowner.');
      }
    } catch (e) {
      alert('Failed to accept bid');
    } finally {
      setAccepting(null);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center p-8"><p className="text-red-600">{error}</p></div>;
  if (!payload) return <div className="min-h-screen flex items-center justify-center p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <p className="text-sm text-gray-500 mb-2">Presented by {payload.coBranding.realtorName}</p>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Bid comparison</h1>
        <p className="text-gray-600 mb-8">Property: {payload.address}</p>
        <ul className="space-y-6">
          {payload.bids.map((b) => (
            <li key={b.id} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <p className="font-semibold text-gray-900">{b.contractorCompany ?? 'Contractor'}</p>
              <p className="text-lg text-gray-700 mt-2">{b.amountCents != null ? `$${(b.amountCents / 100).toFixed(2)}` : 'N/A'}</p>
              {b.scopeNotes && <p className="text-sm text-gray-500 mt-2">{b.scopeNotes}</p>}
              <button
                onClick={() => handleAccept(b.id)}
                disabled={!!accepting}
                className="mt-4 px-4 py-2 bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {accepting === b.id ? 'Accepting...' : 'Accept this bid'}
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-400 mt-8">CoreBids · Client Portal</p>
      </div>
    </div>
  );
};

export default RealtorClientPortal;
// CB:END fr-014-realtor-client-portal
